import React from 'react';
import { Button, Space, theme, Typography } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { MissingTransportRequiredField } from './transportRequiredFields';

const { Text } = Typography;
const COMPACT_VISIBLE_ITEMS = 4;

interface TransportRequiredFieldsChecklistProps {
  missingFields: MissingTransportRequiredField[];
  mode?: 'full' | 'compact';
  onFieldClick: (field: MissingTransportRequiredField) => void;
}

function fieldLabel(
  field: MissingTransportRequiredField,
  t: (key: string, values?: Record<string, unknown>) => string,
): string {
  return t(`transport:requiredSummary.items.${field.kind}`, {
    product: field.productName,
    order: field.orderNumber,
  });
}

const TransportRequiredFieldsChecklist: React.FC<TransportRequiredFieldsChecklistProps> = ({
  missingFields,
  mode = 'full',
  onFieldClick,
}) => {
  const { t } = useTranslation(['transport']);
  const { token } = theme.useToken();
  const isComplete = missingFields.length === 0;

  if (mode === 'compact') {
    // Decision 2026-10-04: the save bar lists what is missing („Brakuje: …”),
    // each item a link to its field; long lists end with „+N”.
    const visible = missingFields.slice(0, COMPACT_VISIBLE_ITEMS);
    const hidden = missingFields.length - visible.length;
    return (
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="transport-required-summary-compact"
        style={{ minWidth: 0 }}
      >
        {isComplete ? (
          <Space size={8}>
            <CheckCircleOutlined style={{ color: token.colorSuccess }} />
            <Text strong>{t('transport:requiredSummary.complete')}</Text>
          </Space>
        ) : (
          <Space size={6} wrap data-testid="transport-required-summary-missing">
            <WarningOutlined style={{ color: token.colorWarning }} />
            <Text strong>{t('transport:requiredSummary.missingLabel')}</Text>
            {visible.map((field, index) => (
              <React.Fragment key={field.id}>
                <Button
                  type="link"
                  size="small"
                  onClick={() => onFieldClick(field)}
                  style={{ height: 'auto', padding: 0, whiteSpace: 'normal', textAlign: 'left' }}
                >
                  {fieldLabel(field, t)}
                </Button>
                {index < visible.length - 1 ? <Text type="secondary" style={{ marginInlineStart: -6 }}>,</Text> : null}
              </React.Fragment>
            ))}
            {hidden > 0 ? (
              <Text type="secondary">{t('transport:requiredSummary.moreCount', { count: hidden })}</Text>
            ) : null}
          </Space>
        )}
      </div>
    );
  }

  return (
    <section
      aria-label={t('transport:requiredSummary.title')}
      data-testid="transport-required-summary-full"
      style={{
        width: '100%',
        minWidth: 0,
        border: `1px solid ${isComplete ? token.colorSuccessBorder : token.colorWarningBorder}`,
        borderRadius: token.borderRadiusLG,
        background: isComplete ? token.colorSuccessBg : token.colorWarningBg,
        padding: '14px 16px',
        marginBottom: 16,
      }}
    >
      <Space align="start" size={10}>
        {isComplete ? (
          <CheckCircleOutlined style={{ color: token.colorSuccess, marginTop: 3 }} />
        ) : (
          <WarningOutlined style={{ color: token.colorWarning, marginTop: 3 }} />
        )}
        <div style={{ minWidth: 0 }}>
          <Text strong>
            {isComplete
              ? t('transport:requiredSummary.complete')
              : t('transport:requiredSummary.title')}
          </Text>
          {!isComplete ? (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {missingFields.map((field) => (
                <li key={field.id}>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => onFieldClick(field)}
                    style={{ height: 'auto', padding: 0, whiteSpace: 'normal', textAlign: 'left' }}
                  >
                    {fieldLabel(field, t)}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </Space>
    </section>
  );
};

export default TransportRequiredFieldsChecklist;
